import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { FilterAccordion } from "./FilterAccordion";
import { FacetGroup } from "./FacetGroup";
import { getStatus, readLabel, updateLabel, updatePosition, updateStatus } from "../tracking/FeatureTracking";
import type { FeatureStatus } from "../tracking/Tracking";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { FilterTitle } from "./FilterTitle";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import { UpdateReferencedAction } from "./UpdateReferencedAction";
import { persistPreSubmitTrackingLabels, setChangeLabels } from "./FilterTracking";
import { FilterRelevancy } from "./FilterRelevancy";
import { FilterlistExpander } from "../filterlistExpander/FilterlistExpander";
import { isMobile } from "../util/Breakpoint";
import { updateSanPopularFacetValues } from "./SveltePopularFacetValues";
import { element } from "../util/Utils";
import { events } from "../events";
import { mergeLabel } from "../tracking/GlobalTracking";
import { SvelteFacet } from "./SvelteFacet";
import { isActive } from "../toggle/Toggle";
import { OcFilterAccordion } from "./OcFilterAccordion";

const FILTER_CLASS = "find_filter";
const BANNER_FILTER_CLASS = "find_filter--banner";

export class Filter {
  static readonly factory = HeurekaElementFactory.byElement("form", FILTER_CLASS, Filter);

  /*               */
  constructor(readonly form: HTMLFormElement) {}

  /*                  */

  static filterId(id: string, root?: NonElementParentNode) {
    return Filter.factory.byId(`find_filter_${id}`, root);
  }

  /*               */
  static register() {
    events.onFilterSectionLoaded(Filter.initAll);
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    Filter.factory.forEach((filter) => filter.init(), rootElement);
  }

  static prepareAll(fragment: ParentNode, initialLoad = false) {
    Filter.factory.forEach((filter) => filter.prepare(initialLoad, fragment), fragment);
  }

  private prepare(initialLoad: boolean, fragment: ParentNode) {
    const isExpanderClosed = !FilterlistExpander.filterListExpander()?.expanded;
    this.isRelevant = FilterRelevancy.template(fragment)?.isRelevant(this.id) || this.isRelevant;
    this.isIrrelevant = FilterRelevancy.template(fragment)?.isIrrelevant(this.id) || this.isIrrelevant;
    this.filterVisibility = this.isInBanner ? "banner" : this.isDenseModeFilter ? "dense" : "expanded";
    this.forceIndex = Filter.factory.byId(this.id)?.forceIndex || undefined;
    const facets = SvelteFacet.factory.all(this.form);
    if (
      this.filterVisibility === "expanded" && /*                                        */
      isExpanderClosed &&
      !facets.some((facet) => facet.selected) && /*                         */
      !this.hidden() /*                                     */
    ) {
      const featureStatus = "loaded";
      this.featureStatus = featureStatus;
      facets.forEach((facet) => {
        facet.updateFeatureStatus("visible", featureStatus);
      });
    }
  }

  static onFilterSectionLoaded() {
    Filter.factory.forEach((filter) => filter.updateFilterTitleInlineMode());
  }

  static handleError() {
    Filter.factory.forEach((filter: Filter) => {
      const isReset = filter.resetToPreSubmitState();
      if (isReset) {
        filter.showError();
        filter.filterTitle?.invalidate();
      }
    });
  }

  protected init() {
    UpdateReferencedAction.on(this.form);
    return this;
  }

  /*                       */

  get id(): string {
    return this.form.id.substring("find_filter_".length);
  }

  get svelteFacets(): SvelteFacet[] {
    return SvelteFacet.factory.all(this.form);
  }

  get facetGroups(): FacetGroup[] {
    return FacetGroup.factory.all(this.form);
  }

  get menuBackButton(): HTMLElement | null {
    return element(`.find_filter__footer .find_filterSheet__menuBackButton`, this.form);
  }

  get filterSelectButton(): HTMLButtonElement {
    return this.form?.querySelector(".find_filter__select");
  }

  get filterAccordion() {
    return isActive("HEUREKA_1975_OC_COMPONENTS_MIGRATION")
      ? OcFilterAccordion.filterId(this.id)
      : FilterAccordion.filterId(this.id);
  }

  get filterTitle() {
    return FilterTitle.filterId(this.id);
  }

  get filterMessage(): HTMLElement | null {
    return this.form.querySelector(".find_filterMessage") || null;
  }

  get hasVisiblePopularValues() {
    return this.svelteFacets.some((facet) => facet.sveltePopularFacetValuesWrapper);
  }

  set featureStatus(status: FeatureStatus | undefined) {
    updateStatus(this.form, status);
  }

  updateFeatureStatus(statusToUpdate: FeatureStatus, updatedStatus: FeatureStatus) {
    if (this.featureStatus == statusToUpdate) {
      this.featureStatus = updatedStatus;
    }
  }

  get featureStatus(): FeatureStatus | undefined {
    return getStatus(this.form);
  }

  set position(position: number) {
    updatePosition(this.form, position);
  }

  set sanPopularFacetValues(value: TrackingLabels["san_PopularFacetValues"]) {
    const filterTitle = this.filterTitle;
    const filterAccordion: OcFilterAccordion | FilterAccordion = this.filterAccordion;
    if (filterTitle) {
      filterTitle.sanPopularFacetValues = value;
    }
    if (filterAccordion) {
      filterAccordion.sanPopularFacetValues = value;
    }
    updateSanPopularFacetValues(this.form, value);
    updateSanPopularFacetValues(this.menuBackButton, value);
  }

  mergeLabel<T extends keyof TrackingLabels>(label: T, value: TrackingLabels[T]) {
    const filterTitle = this.filterTitle;
    const filterAccordion = this.filterAccordion;
    if (filterTitle) {
      filterTitle.mergeLabel(label, value);
    }
    if (filterAccordion) {
      filterAccordion.mergeLabel(label, value);
    }
    mergeLabel(this.form, label, value);
    mergeLabel(this.menuBackButton, label, value);
  }

  set filterFoldoutStatus(value: TrackingLabels["san_FilterFoldoutStatus"]) {
    updateLabel(this.form, "san_FilterFoldoutStatus", value);
  }

  /*          */
  get filterFoldoutStatus(): TrackingLabels["san_FilterFoldoutStatus"] | undefined {
    return readLabel(this.form, "san_FilterFoldoutStatus");
  }

  get filterVisibility(): TrackingLabels["san_FilterVisibility"] | undefined {
    return readLabel(this.form, "san_FilterVisibility");
  }

  set filterVisibility(value: TrackingLabels["san_FilterVisibility"]) {
    updateLabel(this.form, "san_FilterVisibility", value);
  }

  set isRelevant(value: boolean) {
    this.form.dataset.isRelevant = value.toString();
  }

  get isRelevant() {
    return this.form.dataset.isRelevant === "true";
  }

  set isIrrelevant(value: boolean) {
    this.form.dataset.isIrrelevant = value.toString();
  }

  get isIrrelevant() {
    return this.form.dataset.isIrrelevant === "true";
  }

  set forceIndex(index: number | undefined) {
    if (index !== undefined) {
      this.form.dataset.forceIndex = index.toString();
    }
  }

  get forceIndex() {
    return this.form.dataset.forceIndex !== undefined ? parseInt(this.form.dataset.forceIndex) : undefined;
  }

  get isInBanner(): boolean {
    return this.form.classList.contains(BANNER_FILTER_CLASS);
  }

  hidden(): boolean {
    return this.form.hidden;
  }

  hiddenStatus(): FeatureStatus | undefined {
    return this.id === "kategorie" || this.id === "kategorien" ? "hidden" : undefined;
  }

  public static updateChangeTrackingLabels(filterId: string, values: Partial<TrackingLabels> | undefined) {
    const filter = Filter.filterId(filterId);
    if (filter) {
      setChangeLabels(filter.form, values);
    }
  }

  clearPosition() {
    this.position = 0;
    return this;
  }

  private updateFilterTitleInlineMode() {
    this.filterTitle?.toggleInlineMode(this.hasVisiblePopularValues);
  }

  /*           */
  private resetToPreSubmitState(): number {
    return persistPreSubmitTrackingLabels(this.form);
  }

  private showError() {
    const msg = this.filterMessage;
    if (msg) {
      /*                                                                   */
      msg.dataset.messageText = "Keine Treffer gefunden.";
      if (isActive("HEUREKA_2221_MIGRATE_TO_OC_BANNER")) {
        msg.hidden = false;
        const text = msg.querySelector<HTMLElement>(".js_filterMessageText");
        if (text) text.textContent = "Keine Treffer gefunden.";
      } else {
        msg.style.display = "";
      }
    }
  }

  public hideError() {
    if (isActive("HEUREKA_2221_MIGRATE_TO_OC_BANNER")) {
      this.filterMessage.hidden = true;
    } else {
      this.filterMessage.style.display = "none";
    }
  }

  get isDenseModeFilter(): boolean {
    return this.isRelevant || this.svelteFacets.some((f) => f.isForcedRelevant);
  }

  toggleInlineMode(force: boolean) {
    const filterTitle = this.filterTitle;
    filterTitle?.toggleInlineMode(force);
    if (isMobile()) {
      /*                                                                                                                         */
      this.filterFoldoutStatus = filterTitle?.onlyPopularValuesAndTitleMode ? "initial_opened" : "closed";
    }
  }

  updateChildrenFeatureStatus(open: boolean) {
    const updatedFeatureStatus: FeatureStatus = open ? "visible" : "loaded";
    const statusToUpdate: FeatureStatus = open ? "loaded" : "visible";
    /*                                                                                                              */
    const activeFacetGroup = this.facetGroups.filter((group) => group.active);
    if (activeFacetGroup && activeFacetGroup.length > 0) {
      activeFacetGroup.forEach((facetGroup) => {
        facetGroup.updateChildrenFeatureStatus(statusToUpdate, updatedFeatureStatus);
      });
    } else {
      this.svelteFacets.forEach((facet) => {
        facet.updateFeatureStatus(statusToUpdate, updatedFeatureStatus);
      });
    }
  }
}
